import { useEffect, useState } from "react";
import {
  storeProducts as MOCK_PRODUCTS,
  storeCategories as MOCK_CATEGORIES,
  type StoreProduct,
} from "@/mocks/storeProducts";

export interface StoreSettings {
  store_name: string;
  currency: string;
  vat_rate: number;
  prices_include_vat: boolean;
  contact_phone: string;
  contact_email: string;
  whatsapp: string;
  delivery: { id: string; label: string; fee: number; active: boolean }[];
  free_delivery_over: number;
  payments: {
    mpesa: { active: boolean; type: string; number: string; account: string; name: string };
    bank: { active: boolean; details: string };
    cod: { active: boolean; label: string };
    quote: { active: boolean; label: string };
  };
  terms: string;
  stk_available: boolean;
}

export const DEFAULT_SETTINGS: StoreSettings = {
  store_name: "ISMAN Engineering Store", currency: "KES", vat_rate: 16, prices_include_vat: false,
  contact_phone: "072 411 4555", contact_email: "orders@isman.co.ke", whatsapp: "254724114555",
  delivery: [
    { id: "pickup", label: "Collect from our Nairobi workshop", fee: 0, active: true },
    { id: "nairobi", label: "Delivery within Nairobi", fee: 1000, active: true },
    { id: "kenya", label: "Delivery countrywide (courier)", fee: 2500, active: true },
  ],
  free_delivery_over: 0,
  payments: {
    mpesa: { active: true, type: "till", number: "", account: "", name: "ISMAN ENGINEERING" },
    bank: { active: false, details: "" },
    cod: { active: true, label: "Pay on delivery / on collection" },
    quote: { active: true, label: "Request an official quotation / invoice first" },
  },
  terms: "", stk_available: false,
};

interface ApiProduct extends Partial<StoreProduct> { id: number; images?: string[]; stock?: number | null; sku?: string }

const toProduct = (p: ApiProduct): StoreProduct => ({
  id: Number(p.id),
  name: String(p.name ?? ""),
  category: String(p.category ?? ""),
  price: Number(p.price ?? 0),
  originalPrice: p.originalPrice ? Number(p.originalPrice) : undefined,
  image: String(p.image || "/images/kitchen/worktops.webp"),
  images: Array.isArray(p.images) ? p.images.filter(Boolean).map(String) : [],
  description: String(p.description ?? ""),
  features: Array.isArray(p.features) ? p.features.map(String) : [],
  badge: p.badge ? String(p.badge) : undefined,
  inStock: p.inStock !== false,
  unit: String(p.unit ?? "per unit"),
  stock: p.stock ?? null,
  sku: p.sku ?? "",
});

/**
 * Live catalogue from the ISMAN store (managed at admin.isman.co.ke/store).
 * Falls back to the bundled catalogue if the store API cannot be reached.
 */
export function useStoreProducts() {
  const [products, setProducts] = useState<StoreProduct[]>(MOCK_PRODUCTS);
  const [categories, setCategories] = useState<string[]>(MOCK_CATEGORIES);
  const [settings, setSettings] = useState<StoreSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [live, setLive] = useState(false);

  useEffect(() => {
    let active = true;
    fetch("/api/store/catalogue.php", { cache: "no-cache" })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!active || !j?.ok) return;
        if (Array.isArray(j.products) && j.products.length) {
          setProducts(j.products.map(toProduct));
          const cats: string[] = Array.isArray(j.categories) && j.categories.length ? j.categories : Array.from(new Set(j.products.map((p: ApiProduct) => String(p.category))));
          setCategories(["All Products", ...cats]);
        }
        if (j.settings) setSettings({ ...DEFAULT_SETTINGS, ...j.settings, payments: { ...DEFAULT_SETTINGS.payments, ...(j.settings.payments || {}) } });
        setLive(true);
      })
      .catch(() => { /* keep fallback */ })
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, []);

  return { products, categories, settings, loading, live };
}
